import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { DEFAULT_LOCALE, INTL_LOCALE, Locale } from '@/enums/locale'
import { Module, ModuleRole } from '@/enums/module'
import i18n, { isLocale } from '@/libs/i18n'
import type { IMe } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const stored       = localStorage.getItem('locale')
    const locale       = ref<Locale>(isLocale(stored) ? stored : DEFAULT_LOCALE)
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<IMe>()

    /*
     * Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() — never a
     * second source of truth. Two modes only, 'light' | 'dark' — no System option in the UI
     * (vue-spec theme.md). Without `emitAuto` the ref always reads the RESOLVED mode, so a
     * first visit (nothing stored yet) shows whatever the OS prefers.
     */
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    // Keep the breakpoint LIVE: read once, `isMobile` would freeze the form factor (and
    // the mobile drawer) until a reload. Bound to the store's effect scope.
    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    /*
     * What the person reading this may do, from their modules (docs/API.md → Permissions). Every
     * answer is false until /me has come back — a screen that draws a button and takes it
     * away a moment later is worse than one that waits. The API enforces the same rules on
     * every write; these only keep somebody from being offered a button that would answer 403.
     *
     * Being a portal administrator (users, settings) and a role inside a module are separate
     * questions: an administrator without a module does not see that module.
     */
    const isPortalAdmin = () => userData.value?.portal_admin === true

    /** The person's role in a module, or null when the module is switched off for them. */
    const moduleRole = (module: Module): ModuleRole | null => {
        const role = userData.value?.modules?.[module]

        return role === ModuleRole.Manager || role === ModuleRole.Member ? role : null
    }

    const hasModule        = (module: Module) => moduleRole(module) !== null
    const isNalogeManager  = () => moduleRole(Module.Naloge) === ModuleRole.Manager
    const canCreateProject = () => isNalogeManager()

    /*
     * A JWT cookie beside the header, so an attachment opened by <a href> is authenticated too
     * — a browser cannot put an Authorization header on a link (docs/API.md: the API accepts
     * the `JWT` cookie as a fallback).
     */
    const setJWTCookie = (jwt: string) => {
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''

        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax${secure}`
        } else {
            document.cookie = `JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secure}`
        }
    }

    // Restore the cookie on page load if the token is already in localStorage.
    setJWTCookie(JWT.value)

    /*
     * THE PERSON'S LANGUAGE WINS once they are known: `Me.locale` (docs/API.md) is theirs
     * wherever they sign in, and the choice made on the login page only lasts until then.
     */
    const changeLocale = (newLocale: string) => {
        locale.value = isLocale(newLocale) ? newLocale : DEFAULT_LOCALE
        localStorage.setItem('locale', locale.value)
        ;(i18n.global.locale as unknown as { value: string }).value = locale.value
        document.documentElement.lang = INTL_LOCALE[locale.value]
    }

    const setUserData = (data: IMe) => {
        userData.value = data

        if (isLocale(data.locale) && data.locale !== locale.value) changeLocale(data.locale)
    }

    const setLogin = (jwt: string, user?: IMe) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)

        if (user) setUserData(user)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value      = ''
        userData.value = undefined
        setJWTCookie('')
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    return {
        JWT, locale, userData, loaderActive, windowWidth, colorMode,
        getJWT, isLoggedIn, isLoaderActive, isMobile,
        isPortalAdmin, moduleRole, hasModule, isNalogeManager, canCreateProject,
        setLogin, setLogout, setUserData, startLoading, stopLoading, changeLocale,
    }
})
